import * as React from 'react';
import Area from './Area';
import { Canvas, GroupProps, Vector3 } from '@react-three/fiber';
import {
  AccumulativeShadows,
  Center,
  Environment,
  MapControls,
  OrbitControls,
  OrthographicCamera,
  PivotControls,
  RandomizedLight,
  useGLTF,
  View,
  ViewportProps,
} from '@react-three/drei';
import { create } from 'zustand';
import { Menu, Button, MantineProvider } from '@mantine/core';
import {
  IconPerspective,
  IconPerspectiveOff,
  IconArrowBigUp,
  IconArrowBigLeft,
  IconArrowBigRight,
  IconArrowBigDown,
  IconHomeUp,
  IconHomeDown,
} from '@tabler/icons-react';
import { Matrix4, Mesh } from 'three';

export type Props = {};

type Projection = 'Perspective' | 'Orthographic';
type Side = 'Top' | 'Bottom' | 'Left' | 'Right' | 'Front' | 'Back';
type Which = 'top' | 'middle' | 'bottom';
type Store = { [which in Which]: Side } & {
  projection: Projection;
  setPanelView: (which: Which, view: Side) => void;
  setProjection: (projection: Projection) => void;
};

const useStore = create<Store>((set) => ({
  projection: 'Perspective',
  top: 'Back',
  middle: 'Top',
  bottom: 'Right',
  setPanelView: (which, view) => set({ [which]: view }),
  setProjection: (projection) => set({ projection }),
}));

const MainPanel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<'div'>>(
  ({ children, ...props }, ref) => {
    const projection = useStore((state) => state.projection);
    const setProjection = useStore((state) => state.setProjection);
    return (
      <div ref={ref} className="relative w-full h-full" {...props}>
        <>{children}</>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button>{projection}</Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconPerspective size={14} />} onClick={() => setProjection('Perspective')}>
              Perspective
            </Menu.Item>
            <Menu.Item leftSection={<IconPerspectiveOff size={14} />} onClick={() => setProjection('Orthographic')}>
              Orthographic
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    );
  }
);

const SidePanel = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<'div'> & { which: Which }>(
  ({ which, children }, ref) => {
    const value = useStore((state) => state[which]);
    const setPanelView = useStore((state) => state.setPanelView);
    // console.log('SidePanel', { which, value });
    return (
      <div ref={ref} className="relative w-full h-full">
        <>{children}</>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button>{value}</Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconArrowBigUp size={14} />} onClick={() => setPanelView(which, 'Top')}>
              Top
            </Menu.Item>
            <Menu.Item leftSection={<IconArrowBigDown size={14} />} onClick={() => setPanelView(which, 'Bottom')}>
              Bottom
            </Menu.Item>
            <Menu.Item leftSection={<IconArrowBigLeft size={14} />} onClick={() => setPanelView(which, 'Left')}>
              Left
            </Menu.Item>
            <Menu.Item leftSection={<IconArrowBigRight size={14} />} onClick={() => setPanelView(which, 'Right')}>
              Right
            </Menu.Item>
            <Menu.Item leftSection={<IconHomeUp size={14} />} onClick={() => setPanelView(which, 'Front')}>
              Front
            </Menu.Item>
            <Menu.Item leftSection={<IconHomeDown size={14} />} onClick={() => setPanelView(which, 'Back')}>
              Back
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    );
  }
);

const MATRIX = new Matrix4();

function Scene({
  background = 'white',
  matrix,
  children,
  ...props
}: Omit<GroupProps, 'matrix'> & { background?: string; matrix: Matrix4 }) {
  const { nodes, materials } = useGLTF(
    'https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/models/bricks/model.gltf'
  );
  const node = nodes.bricks as Mesh;
  return (
    <>
      <color attach="background" args={[background]} />
      <ambientLight />
      <directionalLight position={[10, 10, -15]} castShadow shadow-bias={-0.0001} shadow-mapSize={1024} />
      <Environment preset="city" />
      <group
        matrixAutoUpdate={false}
        // Why onUpdate and not just matrix={matrix} ?
        // This is an implementation detail, overwriting (most) transform objects isn't possible in Threejs
        // because they are defined read-only. Therefore Fiber will always call .copy() if you pass
        // an object, for instance matrix={new THREE.Matrix4()} or position={new THREE.Vector3()}
        // In this rare case we do not want it to copy the matrix, but refer to it.
        onUpdate={(self) => (self.matrix = matrix)}
        {...props}
      >
        <Center>
          <mesh castShadow geometry={node.geometry} material={materials['Stone.014']} rotation={[Math.PI / 2, 0, 0]}>
            <meshStandardMaterial color="goldenrod" roughness={0} metalness={1} />
          </mesh>
        </Center>
        {children}
      </group>
    </>
  );
}

const positions: { [side in Side]: Vector3 } = {
  Top: [0, 10, 0],
  Bottom: [0, -10, 0],
  Left: [-10, 0, 0],
  Right: [10, 0, 0],
  Back: [0, 0, -10],
  Front: [0, 0, 10],
};

function PanelCamera({ which }: { which: Which }) {
  const view = useStore((state) => state[which]);
  return <OrthographicCamera makeDefault position={positions[view]} zoom={100} />;
}

function App01(props: Props) {
  const {} = props;
  return (
    <MantineProvider>
      <Area className="relative">
        {(el) => (
          <>
            <Canvas
              style={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
              eventSource={el}
            >
              <View.Port />
            </Canvas>
            <div className="top-0 left-0 grid grid-cols-2 grid-rows-3 w-full h-full gap-2">
              <div className="border border-red-500 row-span-3 col-start-1 row-start-1">
                <MainPanel>
                  <View className="absolute top-0 left-0 w-full h-full">
                    <Scene background="aquamarine" matrix={MATRIX} />
                    <OrbitControls makeDefault />
                    <PivotControls scale={1} depthTest={false} matrix={MATRIX} />
                  </View>
                </MainPanel>
              </div>
              <div className="border border-red-500 col-start-2 row-start-1">
                <SidePanel which="top">
                  <View className="absolute w-full h-full">
                    <PanelCamera which="top" />
                    <Scene background="lightpink" matrix={MATRIX} />
                    <MapControls makeDefault screenSpacePanning enableRotate={false} />
                  </View>
                </SidePanel>
              </div>
              <div className="border border-red-500 col-start-2 row-start-2">
                <SidePanel which="middle">
                  <View className="absolute w-full h-full">
                    <PanelCamera which="middle" />
                    <Scene background="peachpuff" matrix={MATRIX} />
                    <MapControls makeDefault screenSpacePanning enableRotate={false} />
                  </View>
                </SidePanel>
              </div>
              <div className="border border-red-500 col-start-2 row-start-3">
                <SidePanel which="bottom">
                  <View className="absolute w-full h-full">
                    <PanelCamera which="bottom" />
                    <Scene background="skyblue" matrix={MATRIX} />
                    <MapControls makeDefault screenSpacePanning enableRotate={false} />
                  </View>
                </SidePanel>
              </div>
            </div>
          </>
        )}
      </Area>
    </MantineProvider>
  );
}

export default React.memo(App01);
